import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Phone, Loader2, Sparkles } from "lucide-react";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour 👋 Je suis votre assistant IA Xpert Recruit. Posez-moi n'importe quelle question sur l'app !" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const [phone, setPhone] = useState("+15149099479");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const newMsgs = [...messages, { role: "user", content: text }];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", { message: text, history: newMsgs.slice(-6) });
      setMessages([...newMsgs, { role: "assistant", content: data.reply }]);
      if (data.escalate) {
        setEscalate(true);
        setPhone(data.support_phone || "+15149099479");
      }
    } catch (e) {
      setMessages([...newMsgs, { role: "assistant", content: "Désolé, je n'arrive pas à répondre pour le moment. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  const smsHref = `sms:${phone}?body=${encodeURIComponent("Bonjour, j'ai un problème sur Xpert Recruit AI :")}`;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 z-50"
          data-testid="support-chat-open"
          aria-label="Ouvrir l'assistant"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] bg-white border border-slate-200 shadow-2xl flex flex-col z-50" data-testid="support-chat-panel">
          <div className="bg-[#0F172A] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-300" />
              <div>
                <div className="font-display text-sm">Assistant Xpert Recruit</div>
                <div className="text-[10px] text-slate-400">Propulsé par IA · 24/7</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-800" data-testid="support-chat-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm ${m.role === "user" ? "bg-[#2563EB] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> L'assistant tape…
                </div>
              </div>
            )}
            {escalate && (
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <div className="font-medium mb-2">⚠️ Problème qui dépasse l'IA ?</div>
                <p className="mb-3">Contactez le support humain directement par SMS :</p>
                <a href={smsHref} className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 text-xs font-medium rounded" data-testid="support-sms-btn">
                  <Phone className="w-3 h-3" /> Envoyer SMS au {phone}
                </a>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-slate-200 bg-white flex items-center gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Votre question…"
              className="h-9 text-sm"
              disabled={loading}
              data-testid="support-chat-input"
            />
            <Button onClick={send} disabled={loading || !input.trim()} className="bg-[#2563EB] hover:bg-[#1D4ED8] h-9 px-3" data-testid="support-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
